import React from 'react'
import { motion } from 'framer-motion'
import { 
  Smartphone, 
  Monitor, 
  Cloud, 
  GitBranch, 
  Database,
  Zap,
  Shield,
  Layers,
  ArrowRight,
  Code,
  Cpu,
  HardDrive
} from 'lucide-react'

const Architecture: React.FC = () => {
  const techStack = [
    {
      category: "Frontend",
      icon: Code,
      technologies: [
        { name: "SwiftUI", description: "Native iOS/macOS UI framework" },
        { name: "WatchKit", description: "Apple Watch development" },
        { name: "React", description: "Web application framework" },
        { name: "TypeScript", description: "Type-safe development" }
      ]
    },
    {
      category: "Backend & Sync",
      icon: Cloud,
      technologies: [
        { name: "CloudKit", description: "Apple's cloud database service" },
        { name: "Core Data", description: "Local data persistence" },
        { name: "GitHub API", description: "Publishing integration" },
        { name: "Speech Framework", description: "Voice-to-text conversion" }
      ]
    },
    {
      category: "Development",
      icon: Cpu,
      technologies: [
        { name: "Xcode", description: "iOS/macOS development environment" },
        { name: "Vite", description: "Fast web build tool" },
        { name: "Swift Package Manager", description: "Dependency management" },
        { name: "GitHub Actions", description: "CI/CD pipeline" }
      ]
    }
  ]

  const dataFlow = [
    {
      step: 1,
      title: "Capture",
      description: "Voice recording on Apple Watch or text input on any device",
      icon: Smartphone,
      color: "bg-blue-100 text-blue-600"
    },
    {
      step: 2,
      title: "Process",
      description: "Speech-to-text conversion and automatic metadata extraction",
      icon: Cpu,
      color: "bg-purple-100 text-purple-600"
    },
    {
      step: 3,
      title: "Store",
      description: "Local Core Data storage with CloudKit synchronization",
      icon: HardDrive,
      color: "bg-green-100 text-green-600"
    },
    {
      step: 4,
      title: "Sync",
      description: "Real-time synchronization across all connected devices",
      icon: Cloud,
      color: "bg-cyan-100 text-cyan-600"
    },
    {
      step: 5,
      title: "Develop",
      description: "Markdown editing and idea status progression tracking",
      icon: Layers,
      color: "bg-orange-100 text-orange-600"
    },
    {
      step: 6,
      title: "Publish",
      description: "Direct publishing to GitHub repositories and static sites",
      icon: GitBranch,
      color: "bg-red-100 text-red-600"
    }
  ]

  const architecturePrinciples = [
    {
      icon: Shield,
      title: "Privacy First",
      description: "Local-first architecture with optional cloud sync. Your data stays under your control."
    },
    {
      icon: Zap,
      title: "Performance Optimized",
      description: "Native apps for maximum performance. Efficient data structures and caching."
    },
    {
      icon: Layers,
      title: "Modular Design",
      description: "Clean separation of concerns with MVVM architecture and reactive programming."
    },
    {
      icon: Database,
      title: "Scalable Storage",
      description: "Core Data with CloudKit provides unlimited scalability and offline support."
    }
  ]

  return (
    <section id="architecture" className="section-padding bg-gray-50">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Robust <span className="gradient-text">Architecture</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Built with modern technologies and best practices for reliability, performance, and scalability.
          </p>
        </motion.div>

        {/* System Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-8 shadow-lg mb-16"
        >
          <h3 className="text-2xl font-bold text-center mb-8">System Overview</h3>
          
          <div className="grid lg:grid-cols-3 gap-8 items-center">
            {/* Client Devices */}
            <div className="text-center">
              <h4 className="text-lg font-semibold mb-4 text-gray-700">Client Devices</h4>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                    <span className="text-sm">⌚</span>
                  </div>
                  <span className="text-sm font-medium">Apple Watch</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                  <Smartphone size={16} className="text-gray-600" />
                  <span className="text-sm font-medium">iPhone/iPad</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 flex items-center space-x-3">
                  <Monitor size={16} className="text-gray-600" />
                  <span className="text-sm font-medium">Mac/Web</span>
                </div>
              </div>
            </div>

            {/* Arrows */}
            <div className="hidden lg:flex flex-col items-center space-y-4">
              <ArrowRight className="text-primary-500" size={24} />
              <ArrowRight className="text-primary-500 rotate-180" size={24} />
            </div>

            {/* Cloud Services */}
            <div className="text-center">
              <h4 className="text-lg font-semibold mb-4 text-gray-700">Cloud Services</h4>
              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-3 flex items-center space-x-3">
                  <Cloud size={16} className="text-blue-600" />
                  <span className="text-sm font-medium">CloudKit Sync</span>
                </div>
                <div className="bg-green-50 rounded-lg p-3 flex items-center space-x-3">
                  <GitBranch size={16} className="text-green-600" />
                  <span className="text-sm font-medium">GitHub API</span>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 flex items-center space-x-3">
                  <Zap size={16} className="text-purple-600" />
                  <span className="text-sm font-medium">Speech API</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Data Flow */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-center mb-8">Data Flow Pipeline</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataFlow.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm card-hover relative"
              >
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${step.color}`}>
                    <step.icon size={24} />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Step {step.step}</div>
                    <h4 className="text-lg font-semibold">{step.title}</h4>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">{step.description}</p>
                
                {index < dataFlow.length - 1 && (
                  <div className="hidden lg:block absolute -right-3 top-1/2 transform -translate-y-1/2">
                    <ArrowRight className="text-gray-300" size={20} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Technology Stack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-center mb-8">Technology Stack</h3>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {techStack.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4">
                    <category.icon className="text-primary-600" size={24} />
                  </div>
                  <h4 className="text-xl font-semibold">{category.category}</h4>
                </div>
                
                <div className="space-y-4">
                  {category.technologies.map((tech, techIndex) => (
                    <div key={techIndex} className="border-l-2 border-primary-200 pl-4">
                      <h5 className="font-medium text-gray-900">{tech.name}</h5>
                      <p className="text-sm text-gray-600">{tech.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Architecture Principles */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary-50 to-green-50 rounded-2xl p-8"
        >
          <h3 className="text-2xl font-bold text-center mb-8">
            <span className="gradient-text">Architecture Principles</span>
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {architecturePrinciples.map((principle, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <principle.icon className="text-primary-600" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">{principle.title}</h4>
                    <p className="text-gray-600">{principle.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Architecture
